import uiConfig from 'src/lib/ui/config';
import { animate, ImageCoverView } from '@play-co/timestep-core/ui';
import ResponsiveScene from './ResponsiveScene';
import StateObserver from 'src/StateObserver';
import { Transition } from 'src/game/logic/SceneManager';
import statePromise from 'src/lib/statePromise';
import Match3Board from '../minigame/match3/components/Match3Board';
import { ReadyStateOverlay } from '../minigame/match3/components/Match3ReadyStateOverlay';
import { MinigameHud } from '../components/MinigameHud';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { createEmitter } from 'src/lib/Emitter';
import { animDuration } from 'src/lib/utils';
import { Match3 } from 'src/replicant/minigame/match3';

const style = uiConfig.buruburu.minigame;

export default class MinigameScene extends ResponsiveScene {
  private hud: MinigameHud;
  private match3Board: Match3Board;
  private overlay: ReadyStateOverlay;
  private comboMessage: LangBitmapFontTextView;
  private pointsMessage: LangBitmapFontTextView;

  constructor() {
    super(
      new ImageCoverView({
        ...uiConfig.buruburu.backgroundDefaults,
        ...style.scene,
      }),
    );

    const view = this.getView();
    const { width, height } = view.style;

    this.hud = new MinigameHud({
      width,
      height: 300,
    });
    view.addSubview(this.hud.view);

    this.match3Board = new Match3Board({
      ...style.match3.board,
      superview: view,
      y: height / 2 - 50, // Move up the board a little bit to clipping at the bottom
    });

    // Disable board until game is ready
    this.match3Board.view.canHandleEvents(false, true);

    this.overlay = new ReadyStateOverlay({ width });
    view.addSubview(this.overlay.view);

    this.comboMessage = view.addSubview(
      new LangBitmapFontTextView(style.comboMessage),
    );
    this.pointsMessage = view.addSubview(
      new LangBitmapFontTextView(style.pointsMessage),
    );

    this.createEmitters();

    view.on('ViewAdded', () => {
      const screenHeight = StateObserver.getState().ui.screenSize.height;
      this.overlay.view.style.height = screenHeight;
      this.overlay.layout();

      this.hud.view.style.height = screenHeight * 0.15;
      this.hud.layout();
    });
  }

  private createEmitters() {
    let comboCounter = 0;

    createEmitter(this.getView(), ({ match3 }) => match3.matching).addListener(
      (isMatching) => {
        if (isMatching) {
          // We've started matching so start counting combos
          this.match3Board.onMatch = async () => {
            if (++comboCounter > 1) {
              this.showComboMessage(comboCounter);
            }
            return false;
          };
        } else {
          // Finished matching so reset combo counter
          comboCounter = 0;
          this.match3Board.onMatch = null;

          // Show total points for this turn
          const { user } = StateObserver.getState();
          const { getLastTurnRewards } = Match3.getters(user);
          const { points } = getLastTurnRewards() ?? {};
          if (points > 0) {
            this.showPointsMessage(Math.round(points));
          }
        }
      },
    );
  }

  async showScene() {
    await statePromise(({ match3 }) => match3.readyState === 'go');
    this.match3Board.view.canHandleEvents(true, false);
  }

  hideScene() {
    this.match3Board.view.canHandleEvents(false, true);
  }

  public transition(): Transition {
    return 'slide_forward';
  }

  private showComboMessage(comboCounter: number) {
    if (this.comboMessage.style.visible) {
      animate(this.comboMessage).clear().then({ opacity: 0 });
    }

    animate(this.comboMessage)
      .then(() => {
        this.comboMessage.updateOpts({
          localeText: () => `${comboCounter} Combos!`,
          visible: true,
          scale: 0,
          opacity: 1,
        });
      })
      .then({ scale: 1 }, animDuration * 2, animate.easeOutElastic)
      .wait(4 * animDuration)
      .then({ opacity: 0 }, animDuration * 2, animate.easeOut)
      .then(() => this.comboMessage.hide());
  }

  private showPointsMessage(points: number) {
    if (this.pointsMessage.style.visible) {
      animate(this.pointsMessage).clear().then({ opacity: 0 });
    }

    animate(this.pointsMessage)
      .then(() => {
        this.pointsMessage.updateOpts({
          localeText: () => `${points} Points!`,
          visible: true,
          scale: 0,
          opacity: 1,
        });
      })
      .then({ scale: 1 }, animDuration * 2, animate.easeOutElastic)
      .wait(3 * animDuration)
      .then({ opacity: 0 }, animDuration * 2, animate.easeOut)
      .then(() => this.pointsMessage.hide());
  }
}
